import request from '@meitu/saas-editor-ui/es/request';
import { CLIENT_ID_POSTER } from '@/types';
import { isInternalDesignerMode } from '@/utils/auth';
import { getWebApiHostname } from '@/utils/request';
import {
    FetchHistoryTagParams,
    FetchMaterialDetailMultiParams,
    FetchMaterialDetailMultiResponse,
    FetchRecommendParams,
    FetchTemplatesParams,
    FetchTemplatesResponse,
    FetchTemplateTypesParams,
    FetchTemplateTypesResponse,
    FetchTopicMaterialsParams,
    FetchTopicMaterialsResponse,
    FilterDetailParams,
    FilterDetailResponse,
    FilterListParams,
    FilterListResponse,
    IFetchMaterialSearchListParams,
    IFetchMaterialSearchListResponse,
    ITextAnimationResponse,
    ITextMarkResponse,
    MaskListListParams,
    MaskListResponse,
    MaterialLayerListResponse,
    MaterialTopicListParams,
    MaterialTopicListResponse,
    MaterialTopiDetailParams,
    MaterialTopiDetailResponse,
    NewMaterialListParams,
    NewMaterialListResponse,
    SaveRecentUsedData,
    SaveRecentUsedQuery,
    SaveRecentUsedResponse,
} from './index.d';

/**
 * 模板类型分类
 * @docs: https://api-mock.meitu-int.com/project/1594/interface/api/83281
 */
export const fetchTemplateTypes = async function (params: FetchTemplateTypesParams) {
    return await request<FetchTemplateTypesResponse>('/operation/material_type_template_data.json', {
        method: 'GET',
        params,
    });
};

/**
 * 接口 [获取蒙版素材]](/operation/material_list.json)
 * @docs: https://api-mock.meitu-int.com/project/1289/interface/api/105537
 */
export const fetchMaskList = async function (params: MaskListListParams) {
    return await request<MaskListResponse>('/seal_pie_internal/material_list.json', {
        method: 'GET',
        params,
    });
};

/**
 * 专题分类列表
 * @docs: https://api-mock.meitu-int.com/project/1594/interface/api/82966
 */
export const fetchTopicMaterials = async function (params: FetchTopicMaterialsParams) {
    return await request<FetchTopicMaterialsResponse>('/operation/category_list.json', {
        method: 'GET',
        params,
    });
};

/**
 * 分类下模板列表
 * @docs: https://api-mock.meitu-int.com/project/1594/interface/api/82980
 */
export const fetchTemplates = async function (params: FetchTemplatesParams) {
    return await request<FetchTemplatesResponse>('/operation/category_materials.json', {
        method: 'GET',
        params,
    });
};

/**
 * 接口 [批量获取素材(返回中台原始数据)↗](/operation/material_detail_multi.json)
 * @docs: https://api-mock.meitu-int.com/project/1594/interface/api/95878
 */
export const fetchMaterialDetailMulti = async function (params: FetchMaterialDetailMultiParams) {
    return await request<FetchMaterialDetailMultiResponse>('/operation/material_detail_multi.json', {
        method: 'GET',
        params,
    });
};

/**
 * 历史记录搜索接口
 * @docs: https://api-mock.meitu-int.com/project/1420/interface/api/101008
 */
export const fetchHistoryTag = async function (params: FetchHistoryTagParams) {
    return await request<FetchTemplatesResponse>('/search/search.json', {
        method: 'GET',
        params,
    });
};

/**
 * 接口 [兜底推荐](/search/recommend.json)
 * @docs: https://api-mock.meitu-int.com/project/1420/interface/api/96594
 */
export const fetchRecommend = async function (params: FetchRecommendParams) {
    return await request<FetchTemplatesResponse>('/search/recommend.json', {
        method: 'GET',
        params,
    });
};

/**
 * 接口 [获取滤镜|素材常用数据](/api/v1/material/recent_used_list)
 * @docs: https://api-mock.meitu-int.com/project/1420/interface/api/96586
 */
export const fetchRecentUsedList = async function (params: FilterDetailParams) {
    return await request<FilterDetailResponse>('/image/api/v1/material/recent_used_list', {
        method: 'GET',
        params,
    });
};

/**
 * 接口 [素材分类列表](/api/v1/material/sticker/special_topic)
 * @docs: https://api-mock.meitu-int.com/project/1420/interface/api/85975
 */
export const fetchMaterialTopicList = async function (params: MaterialTopicListParams) {
    return await request<MaterialTopicListResponse>('/seal_pie_internal/category_list.json', {
        method: 'GET',
        params: {
            ...params,
            code: 'static_sticker_category',
        },
    });
};

/**
 * 接口 [最新素材列表]](/api/v1/material/sticker/newest)
 * @docs: https://api-mock.meitu-int.com/project/1420/interface/api/86599
 */
export const fetchNewMaterialList = async function (params: NewMaterialListParams) {
    return await request<NewMaterialListResponse>('/image/api/v1/material/sticker/newest', {
        method: 'GET',
        params,
    });
};

/**
 * 接口 [素材分类详情](/api/v1/material/sticker/list_with_category)
 * @docs: https://api-mock.meitu-int.com/project/1289/interface/api/105474
 */
export const fetchImageMaterialTopiDetail = async function (params: MaterialTopiDetailParams) {
    return await request<MaterialTopiDetailResponse>('/seal_pie_internal/category_material_list.json', {
        method: 'GET',
        params,
    });
};

/**
 * 接口 [滤镜分类列表](/api/v1/material/filter/category)
 * @docs: https://api-mock.meitu-int.com/project/1420/interface/api/93811
 */
export const fetchFilterList = async function (params: FilterListParams) {
    return await request<FilterListResponse>('/seal_pie_internal/category_list.json', {
        method: 'GET',
        params: {
            ...params,
            code: 'image_filter_category',
        },
    });
};

/**
 * 接口 [滤镜分类素材](/api/v1/material/filter/list_with_category)
 * @docs: https://api-mock.meitu-int.com/project/1420/interface/api/93818
 */
export const fetchFilterTopiDetail = async function (params: FilterDetailParams) {
    return await request<FilterDetailResponse>('/image/api/v1/material/filter/list_with_category', {
        method: 'GET',
        params,
    });
};

/**
 * 接口 [保存|保存使用的素材](/api/v1/material/save_recent_used)
 * @docs: https://api-mock.meitu-int.com/project/1420/interface/api/96594
 */
export const fetchSaveRecentUsed = async function (query: SaveRecentUsedQuery, data: SaveRecentUsedData) {
    return await request<SaveRecentUsedResponse>('/image/api/v1/material/save_recent_used', {
        method: 'POST',
        params: { ...query, client_id: CLIENT_ID_POSTER },
        data,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    });
};

/**
 * https://api-mock.meitu-int.com/project/2110/interface/api/138442  获取已打标的素材列表
 */
export const fetchMaterialLayerList = async function () {
    return await request<MaterialLayerListResponse>(`${getWebApiHostname()}/v1/material_layer/list`, {
        method: 'get',
    });
};

/**
 * https://api-mock.meitu-int.com/project/1289/interface/api/142298
 */
export const fetchMaterialSearchList = async (params: IFetchMaterialSearchListParams) => {
    return await request<IFetchMaterialSearchListResponse>(`/seal_pie_internal/search`, {
        method: 'get',
        params,
    });
};

/**
 * 划重点素材 [已预处理↗](/v1/material/list)
 * @docs: https://api-mock.meitu-int.com/project/2110/interface/api/113380
 */
export const fetchTextMark = async function () {
    try {
        const res = await request<ITextMarkResponse>(`/seal_pie_internal/material_list.json`, {
            method: 'GET',
            params: {
                type: 'highlight_key_point',
                limit: 100,
            },
            hideErrorToast: true,
        });

        if (res.data.meta.code === 0) {
            return res.data.response.materials
                .map((item) => {
                    const {
                        id: materialId,
                        threshold,
                        material_conf: data,
                        thumbnail,
                        animate,
                        thumbnail_black,
                    } = item as any;

                    return {
                        materialId,
                        isVip: !!threshold,
                        data,
                        thumbnail,
                        animate,
                        thumbnailBlack: thumbnail_black,
                    };
                })
                .filter((item) => {
                    return (item.animate && isInternalDesignerMode()) || !item.animate;
                });
        }
        return [];
    } catch (error) {
        console.log(error);
        return [];
    }
};

export const fetchTextAnimationMaterial = async function (type: string) {
    return await request<ITextAnimationResponse>(`/seal_pie_internal/material_list.json`, {
        method: 'GET',
        params: {
            type,
            limit: 2000,
        },
    });
};

/**
 * 接口 [滤镜分类素材](/api/v1/material/filter/list_with_category)
 *
 * {@link https://api-mock.meitu-int.com/project/1420/interface/api/93818}
 * @param params - 请求参数
 */
export const fetchFilterTopicDetail = async function (params: FilterDetailParams) {
    return await request<FilterDetailResponse>(`${getWebApiHostname()}/v1/category/materials`, {
        method: 'GET',
        params,
    });
};

/**
 * https://api-mock.meitu-int.com/project/2110/interface/api/187095
 * @returns
 */
export const fetchSvgMaterialDetail = async (id: string) => {
    return await request<any>(`${getWebApiHostname()}/v1/material/detail`, {
        method: 'GET',
        hideErrorToast: true,
        params: { id, client_id: '1189857523' },
    });
};

export const updateSvgMaterial = async (material_id: string, svg_file: string, svg_image: string, preview: string) => {
    return await request<any>(`${getWebApiHostname()}/v1/material_layer/update_pic_static_sticker`, {
        method: 'POST',
        params: { client_id: '1189857523' },
        data: {
            material_id,
            svg_file,
            svg_image,
            preview,
        },
    });
};
